import { useState } from 'react';

import { contactsApi } from '@/api/queries/contacts';
import type { Contact } from '@/api/types/contacts';
import { Button, Icon, SectionCard, Tag, useToast } from '@/components/ui';

/**
 * Comment thread for an existing contact — distinct from the read-only Notes.
 * Users post messages here; the hourly ContactReviewJob leaves system comments
 * (kind === "system") which are rendered with a distinct badge.
 */
export function CommentsSection({ contact }: { contact: Contact }) {
  const toast = useToast();
  const addComment = contactsApi.useAddComment();
  const id = contact.id ?? '';
  const [text, setText] = useState('');

  // Newest first.
  const comments = [...(contact.comments ?? [])].reverse();

  const submit = async () => {
    const trimmed = text.trim();
    if (!trimmed) return;
    await addComment.mutateAsync({ id, text: trimmed });
    setText('');
    toast.show({ title: 'Comment added', severity: 'success' });
  };

  return (
    <SectionCard title="Comments">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <textarea
          className="ds-input"
          rows={3}
          placeholder="Leave a comment…"
          value={text}
          onChange={(e) => setText(e.target.value)}
          style={{ width: '100%', resize: 'vertical' }}
        />
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="secondary"
            size="sm"
            leftIcon={<Icon name="send" size={13} />}
            disabled={!text.trim() || addComment.isPending}
            onClick={submit}
          >
            Add comment
          </Button>
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 14 }}>
        {comments.length === 0 && (
          <span style={{ fontSize: 12, color: 'var(--text-3)' }}>No comments yet.</span>
        )}
        {comments.map((c, i) => {
          const system = c.kind === 'system' || c.createdBy?.startsWith('system:');
          return (
            <div
              key={i}
              style={{
                padding: 10,
                borderRadius: 8,
                background: system ? 'var(--surface-2)' : 'var(--surface-1)',
                border: '1px solid var(--border)',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                {system && <Tag tone="brand">Auto</Tag>}
                <span style={{ fontSize: 11, color: 'var(--text-3)' }}>
                  {c.createdBy}
                  {c.createdAt && ` · ${new Date(c.createdAt).toLocaleString()}`}
                </span>
              </div>
              <div style={{ fontSize: 13, color: 'var(--text-1)', whiteSpace: 'pre-wrap' }}>
                {c.text}
              </div>
            </div>
          );
        })}
      </div>
    </SectionCard>
  );
}
